import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage11() {
  const contentRef = useRef(null);
     const links = {
    prev: "/islamic-trade-page-10", 
    index: "/index",                 
    next: "/islamic-trade-page-12", 
  };
  const titleText =
    "Types of Items in Loans and The Impact of Inflation on them";
  const questions = [
    {
      text: "1. In Islamic jurisprudence, how must a loan involving fungible (mithliyyat) items be repaid??",
      options: [
        {
          value: "A",
          label: "A. By offering a different item of equal value.",
        },
        {
          value: "B",
          label:
            "B. By returning the exact item lent, regardless of condition.",
        },
        {
          value: "C",
          label:
            "C. By returning the same kind, quality, and quantity of the item.",
        },
        {
          value: "D",
          label: "D. By paying the monetary value at the time of lending.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. When can a loanee repay the value of a fungible item instead of its kind?",
      options: [
        { value: "A", label: "A. When the lender demands early repayment." },
        {
          value: "B",
          label: "B. When the item becomes available at a lower price.",
        },
        {
          value: "C",
          label:
            "C. When the item is unavailable or its price has risen exponentially.",
        },
        { value: "D", label: "D. When the loanee wants to avoid using cash." },
      ],
      correct: "C",
    },
    {
      text: "3. Which of the following is true regarding non-fungible (mutaqawwimat) items in a loan?",
      options: [
        {
          value: "A",
          label: "A.  They must always be returned in the same physical form.",
        },
        {
          value: "B",
          label: "B.  They are repaid with similar items of the same type.",
        },
        {
          value: "C",
          label: "C. They are repaid by their agreed-upon market value.",
        },
        { value: "D", label: "D. They cannot be used in Islamic loans." },
      ],
      correct: "A",
    },
    {
      text: "4.  According to Islamic ruling, when is it permissible to repay a loan in a different currency?",
      options: [
        { value: "A", label: "A. When the lender insists." },
        { value: "B", label: "B. When stipulated in the loan contract." },
        {
          value: "C",
          label:
            "C. When inflation has significantly reduced the original currency’s value.",
        },
        { value: "D", label: "D. A loan with interest" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={11}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 11</b>! Today, we will delve deeper into the
        intricacies of loans by examining the types of items that can be loaned
        and how Islamic jurisprudence addresses their repayment. We will explore
        the concept of fungible items i.e those that can be replaced by an
        identical counterpart and understand the rules that apply when these
        items become unavailable or undergo drastic changes in value. We'll also
        look at non-fungible items, which are unique and must be repaid based on
        their monetary worth rather than a like-for-like replacement.
        Additionally, we will study the effects of inflation and currency
        changes on loan repayments and the importance of fairness and accuracy
        in such exchanges. Finally, we will emphasize one of the most critical
        principles in Islamic finance: the prohibition of deriving any material
        benefit from a loan, ensuring that lending remains a sincere act of
        support rather than a means of profit.
        <br />
        <br /> <b>The Moral and Legal Foundation of Loans in Islam</b>
        <br />
        <br />
        We discussed Loans within the framework of Ilsamic Jurispudence in the
        preceding section, however; let us revise in brief what was covered in
        the previous lesson, since our topic for today is an extension of what
        we learned yesterday.
        <br />
        Loans hold a deeply ethical and spiritual dimension in Islam. They are
        not commercial transactions designed to generate profit, but acts of
        compassion intended to alleviate hardship and strengthen social bonds.
        When a Muslim gives a loan, it is viewed as a charitable act, an
        expression of kindness and mutual aid. This perspective stands in
        contrast to conventional financial systems where loans are typically
        profit-driven and where lenders expect compensation in the form of
        interest. In the Islamic framework, lending is purely altruistic, and
        the only reward the lender seeks is that which comes from Allah. <br />
        The Prophet Muhammad (peace be upon him) emphasized this moral
        foundation when he said, <br />
        “He who alleviates the suffering of a brother out of the sufferings of
        the world, Allah would alleviate his suffering from the sufferings of
        the Day of Resurrection, and he who finds relief for one who is
        hard-pressed, Allah would make things easy for him in the Hereafter, and
        he who conceals (the faults) of a Muslim, Allah would conceal his faults
        in the world and in the Hereafter”
        <a href="https://sunnah.com/muslim:2699a" target="blank">
          (Sahih Muslim 2699)
        </a>{" "}
        <br /> Through such teachings, Islam transforms the notion of lending
        from a business transaction into a form of worship, an act of mercy that
        brings spiritual reward.
        <br />
        This approach reflects a profound moral wisdom. A lender, under Islamic
        ethics, should not be concerned about inflation, currency fluctuation,
        or market volatility when extending a loan. The loan is a trust between
        two individuals, not an investment. The lender’s satisfaction should
        come from helping someone in need, not from preserving purchasing power
        or earning a return. If the loan is repaid after a long time, and the
        value of the currency has decreased, the lender still receives exactly
        what was lent. In this sense, loans in Islam are governed by the
        principle of mutual benevolence rather than economic gain.
        <br />
        At the heart of this teaching lies the Qur’anic command:
        <br /> “O believers! Do not devour one another’s wealth illegally, but
        rather trade by mutual consent. And do not kill ˹each other or˺
        yourselves. Surely Allah is ever Merciful to you.”
        <a href="https://quran.com/4/29" target="blank">
          (Surah An-Nisa 30)
        </a>
        <br /> This verse highlights that any form of wealth transfer must be
        founded upon fairness and agreement, not coercion or hidden advantage.
        It serves as the ethical cornerstone for all Islamic financial dealings.
        When Muslims lend and borrow within these moral boundaries, they uphold
        justice and compassion, ensuring that economic relationships do not
        become instruments of oppression.
        <br />
        <br />
        <b>
          Fungible Items(Mithliyyat) and the Principle of Repayment in Kind
        </b>{" "}
        <br />
        <br />
        Islamic jurisprudence draws a careful distinction between items that are
        fungible and those that are not. Fungible items are goods that can be
        replaced by another identical item of the same type, quality, and
        quantity. They are interchangeable in nature, such as wheat, rice, gold,
        silver, oil, or standard currencies. In such cases, the borrower repays
        the lender with the same kind of goods rather than with their monetary
        value. For example, if someone borrows ten kilograms of rice, they must
        return ten kilograms of rice of similar quality. This rule ensures that
        the loan remains fair and that neither party benefits or suffers due to
        market changes.
        <br />
        However, practical life often brings situations where the borrowed item
        becomes unavailable or undergoes drastic changes in price. Islamic law
        addresses these situations with clarity and fairness. If the specific
        item becomes unavailable in the market, the borrower may repay its fair
        market value at the time it became unavailable. Suppose a man lends his
        neighbor ten sacks of a particular type of wheat, but that variety is no
        longer found in the market when repayment is due. In that case, the
        borrower can repay the value of that wheat as determined by its market
        price at the time of its disappearance. This ensures that the lender
        does not face loss while also not burdening the borrower with an
        impossible obligation.
        <br />
        Another situation arises when the price of a fungible item increases
        exponentially. For instance, if someone borrows a quantity of gold or
        oil, and during the repayment period, the price skyrockets, the question
        of fairness becomes crucial. In such cases, if repayment in kind becomes
        excessively difficult or harmful to the borrower, the value of the item
        at the time of the drastic price increase can be used to settle the
        loan. This adjustment protects the borrower from undue hardship.
        Islam emphasizes balance and compassion in every economic relationship.
        The aim is not to enforce mechanical equality but to ensure justice in
        real terms.
        <br />
        The same principle applies when a currency becomes obsolete or is
        abolished. If a loan is taken in a particular currency, it must normally
        be repaid in that same currency. But if the government bans or replaces
        it due to counterfeiting or devaluation, the borrower can repay the
        equivalent value in another currency. The value of the old currency at
        the time it was abolished serves as the reference for repayment. This
        approach demonstrates how Islamic finance accommodates real-world
        changes while safeguarding ethical integrity. It prevents exploitation
        and preserves the trust between lender and borrower.
        <br />
        The question of inflation is often raised in modern times. Islamic
        jurists maintain that a loan must be repaid in the same currency that
        was borrowed, even if inflation reduces its purchasing power. It is
        impermissible to stipulate at the time of lending that repayment will be
        made in an adjusted amount to compensate for inflation. Such an
        arrangement would effectively introduce interest into the transaction
        because the lender would be gaining a benefit beyond the original
        loan. Through this nuanced approach, Islamic law balances realism with
        righteousness. It acknowledges economic fluctuation but refuses to let
        it justify injustice.
        <br />
        In all these rulings, the spirit of Islam remains clear: loans are acts
        of generosity, not contracts of profit. Any clause that seeks to protect
        the lender from loss in value ultimately turns the loan into a
        commercial transaction, which contradicts the very purpose of lending in
        Islam. The Qur’an’s prohibition of unjust gain is not merely a financial
        regulation but a reminder that believers must place faith in divine
        justice rather than in worldly profit. By repaying exactly what was
        lent, the borrower fulfills their trust, and the lender fulfills their
        moral duty to assist without expectation.
        <br />
        <br />{" "}
        <b>
          Non-Fungible Items (Mutaqawwimat) and the Principle of Fair Valuation
        </b>
        <br />
        <br />
        While fungible items can be repaid in kind, non-fungible items belong to
        a different category altogether. Non-fungible goods are unique and
        cannot be replaced by an identical counterpart. Their value lies in
        their individuality, such as in the case of land, property, vehicles,
        handcrafted goods, or works of art. When these items are loaned, the
        borrower cannot simply return another similar item, because no true
        equivalent exists. Instead, repayment must be based on the fair monetary
        value of the item. Islamic law treats this matter with precision and
        fairness to ensure that neither party is wronged. <br />
        For example, if a person borrows a handcrafted table and it gets
        damaged, the borrower must repay the value of the original table, not
        merely replace it with another table of similar size or color. The value
        must reflect the table’s craftsmanship, material, and unique features.
        Similarly, if someone borrows an animal, a piece of equipment, or a
        vehicle, and it is lost or destroyed, the compensation is calculated
        according to its fair market value at the time of loss. This valuation
        should be objective and based on prevailing market standards, ensuring
        that both parties are treated equitably.
        <br />
        Islamic scholars have long emphasized that fairness in valuation is
        central to maintaining justice in transactions involving non-fungible
        items. Valuation should not be manipulated by either side for personal
        advantage. If the lender inflates the estimated price to demand more, or
        if the borrower undervalues it to pay less, the spirit of justice is
        violated. To prevent such disputes, scholars recommend referring to
        neutral experts or market assessments to determine fair compensation. In
        this way, financial dealings remain transparent and free from
        resentment.
        <br />
        The Prophet Muhammad (peace be upon him) beautifully captured the moral
        essence of fairness in trade and repayment when he said, “May Allah's
        mercy be on him who is lenient in his buying, selling, and in demanding
        back his money”
        <a href="https://sunnah.com/bukhari:2076" target="blank">
          (Sahih Al-Bukhari 2076)
        </a>{" "}
        <br />
        This saying is not only a spiritual reminder but also a legal principle.
        It establishes leniency and kindness as virtues that must guide all
        financial dealings. Islam seeks not only the correct fulfillment of
        contracts but also the cultivation of a generous spirit in human
        interactions.
        <br /> The concept of fairness in non-fungible loans also extends to the
        treatment of time and patience. If a borrower faces difficulty in
        repaying, the lender is encouraged to give them more time rather than
        demanding immediate compensation. <br />
        The Qur’an reinforces this principle in the verse, “If it is difficult
        for someone to repay a debt, postpone it until a time of ease. And if
        you waive it as an act of charity, it will be better for you, if only
        you knew.”
        <a href="https://quran.com/al-baqarah/280" target="blank">
          (Surah Al Baqarah 280)
        </a>{" "}
        <br />
        Such leniency transforms financial dealings into acts of compassion and
        strengthens the moral fabric of society. When people interact with
        empathy rather than greed, economic relationships become sources of
        mutual trust and blessing rather than tension and hostility. In short,
        the rules regarding non-fungible items remind us that Islamic law is not
        rigid but deeply humane. It recognizes the individuality of things and
        the complexity of real-life situations. By insisting on fair valuation
        and leniency, Islam ensures that justice is not merely theoretical but
        practical and lived. Every transaction becomes an opportunity to embody
        moral excellence.
        <br />
        <br />
        <b>The Prohibition of Benefit and the Spirit of Financial Ethics</b>
        <br />
        <br />
        Perhaps the most fundamental principle that governs all loans in Islam
        is the absolute prohibition of deriving any benefit or gain from them.
        This prohibition is not confined to explicit interest but extends to any
        form of material or moral advantage linked to the loan. This captures
        the entire spirit of Islamic financial ethics. A loan is a charitable
        act, not a business opportunity. If the lender expects or receives any
        additional benefit, whether in the form of money, favors, gifts, or
        services, the purity of the act is corrupted, and the transaction falls
        into the category of riba, which the Qur’an condemns in the strongest
        terms.
        <br />
        The Qur’an states, “O believers! Do not consume interest, multiplying it
        many times over. And be mindful of Allah, so you may prosper.”
        <a href="https://quran.com/3/130" target="blank">
          (Surah Ali Imran 130)
        </a>{" "}
        This command does not merely prohibit usurious interest but the mindset
        that seeks profit from another’s need. The lender who uses a loan to
        enrich himself violates the principle of mutual assistance upon which
        Islamic society is built. By contrast, the one who lends out of
        compassion mirrors divine mercy. The Prophet once narrated the story of
        a man who used to forgive the debts of those in difficulty. Because of
        his kindness, Allah forgave the man’s own sins and admitted him to
        Paradise. This story highlights the spiritual value of leniency and
        forgiveness, transforming financial ethics into acts of worship.
        <br />
        In modern contexts, this prohibition requires vigilance. The temptation
        to secure one’s wealth against inflation or to expect gratitude in
        material form can subtly reintroduce riba under different names. For
        example, if a lender expects a gift in return for giving a loan, or if
        the borrower is pressured to repay with something extra as a sign of
        appreciation, the essence of the prohibition is violated. However, if
        after repaying the loan the borrower voluntarily offers a token of
        thanks, without any prior expectation or obligation, it is permissible.
        The distinction lies in intention and timing. What Islam forbids is any
        form of compulsion or precondition that links benefit to the loan.
        <br />
        This approach is not merely about legal precision but about nurturing
        spiritual sensitivity. The purpose of Islamic financial ethics is to
        purify the heart from greed and to create a community bound by trust and
        compassion. The lender should remember that his true reward lies not in
        the repayment itself but in the divine recompense promised by Allah. The
        borrower, on the other hand, must repay with sincerity and punctuality,
        understanding that failing to return a loan without genuine difficulty
        is a moral wrongdoing. Both parties are accountable not only before each
        other but before Allah. <br />
        The Prophet (peace be upon him) said, “The most beloved of people to
        Allah are those who bring the most benefit to others.”(Al-Tabarani,
        Al-Muʿjam al-Awsat, Hadith 5787) <br />
        This hadith sums up the ultimate aim of all Islamic financial
        principles. Wealth, in Islam, is not an end in itself but a means of
        doing good. Loans, when given sincerely, become acts of charity that
        strengthen the bonds of brotherhood. By ensuring that no material
        benefit is attached to them, Islam preserves their purity as instruments
        of kindness. The prohibition of benefit is therefore not a restriction
        but a protection, it shields both the lender and the borrower from moral
        harm.
        <br />
        Through these teachings, Islam establishes a financial system that
        upholds both justice and mercy. It insists that wealth must circulate
        without exploitation, that generosity must remain untainted by greed,
        and that every economic transaction should be a reflection of ethical
        integrity. The laws of fungibility, fairness in valuation, and
        prohibition of benefit all converge to uphold one truth: money and
        material goods are tests of character. Those who use them with honesty
        and compassion fulfill a divine trust. In contrast, those who manipulate
        them for selfish gain undermine the moral foundations of society. Thus,
        when we study the principles governing loans in Islam, we are not merely
        learning financial rules; we are rediscovering the ethical soul of the
        Islamic economic order. It is an order built not on profit and loss but
        on justice and goodwill, not on advantage and leverage but on fairness
        and faith. When Muslims lend without seeking benefit, when they forgive
        the debts of the needy, and when they uphold honesty in repayment, they
        revive the spirit of mercy that Islam seeks to instill in every human
        relationship. In the end, this is the true goal of Islamic finance: not
        to accumulate wealth, but to earn the pleasure of Allah through
        fairness, compassion, and integrity in every exchange.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today’s lesson, we explored the classification of loaned items into
        fungible (mithliyyat) and non-fungible (mutaqawwimat) categories. We
        learned that fungible items like grains, currencies, or metals are to be
        repaid with their exact equivalents, while non-fungible items such as
        property or handcrafted goods must be repaid by their value, due to
        their unique nature. The lesson also covered specific scenarios such as
        unavailability of items, sudden price hikes, or the abolishment of a
        currency, and how Islamic principles guide fair repayment in such cases.
        Additionally, we discussed how inflation impacts loan repayments and why
        it is impermissible to stipulate repayment in a different currency
        unless voluntarily agreed upon at the time of repayment. Finally, we
        emphasized the strict prohibition in Islam against deriving any benefit
        from a loan, reaffirming that loans are acts of charity and compassion,
        not opportunities for profit or exploitation.
        <br />
        <br />
        <b>Coming up next</b> <br />
        <br />
        On Day 12, we will explore the concept of Mortgage (Rahn) in Islam, a
        practical and permissible means of securing loans through pledged
        collateral. We will study how the Qur’an and Sunnah lay the foundation
        for this financial arrangement, and how Islamic law treats different
        types of pledged assets whether tangible property, usufruct, or debt.
        You’ll also learn the rules governing joint ownership, the timing of
        pledges in contracts, and how any increase or compensation related to a
        mortgaged item is handled under Shariah. This lesson will equip you with
        a comprehensive understanding of how Islam ensures fairness and security
        in collateral-based transactions.
      </p>
      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

     <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage11;
